import { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTimeZones } from '../../../../hooks/UseTimeZones';
import { getAllVoices } from '../../../../utils/apis';

interface AgentDefinitionProps {
  definition: any;
  agentType: 'voice' | 'whatsapp' | null;
  onDefinitionChange: (definition: any) => void;
  onValidationChange: (isValid: boolean) => void;
}

const AgentDefinition = ({
  definition,
  agentType,
  onDefinitionChange,
  onValidationChange,
}: AgentDefinitionProps) => {
  const { timeZones, isLoading: timezonesLoading } = useTimeZones();
  const { data: sampleVoices } = useQuery({
    queryKey: ['ultraVoices'],
    queryFn: () => getAllVoices({ service: 'Ultra' }),
  });

  const voicesData = useMemo(
    () => sampleVoices?.data?.data ?? [],
    [sampleVoices?.data?.data]
  );

  const getUniqueLanguages = useMemo(() => {
    if (!voicesData.length) return [];
    const languageSet = new Set<string>();
    voicesData.forEach((voice: { language?: string }) => {
      if (voice.language) languageSet.add(voice.language);
    });
    return Array.from(languageSet).map(language => ({ language }));
  }, [voicesData]);

  const getAvailableVoices = useMemo(() => {
    if (!voicesData.length || !definition?.language) return [];
    return voicesData.filter(
      (voice: { language?: string }) => voice.language === definition.language
    );
  }, [voicesData, definition?.language]);

  useEffect(() => {
    const baseValid =
      !!definition?.name &&
      !!definition?.personality &&
      !!definition?.language &&
      !!definition?.timezone;
    const voiceValid =
      agentType !== 'voice' ||
      !!(
        definition?.voice?.name ??
        (typeof definition?.voice === 'string' && definition?.voice)
      );
    onValidationChange(baseValid && voiceValid);
  }, [definition, agentType, onValidationChange]);

  // Default language and voice when voices load (mirror AssistantDefination)
  useEffect(() => {
    if (voicesData.length === 0) return;
    const defaultLanguage = 'English (US)';
    const hasEnglish = voicesData.some(
      (v: { language?: string }) => v.language === defaultLanguage
    );

    if (!definition?.language) {
      if (!hasEnglish) return;
      const voicesForLanguage = voicesData.filter(
        (v: { language?: string }) => v.language === defaultLanguage
      );
      const cassidyVoice = voicesForLanguage.find(
        (v: { name?: string }) => v.name === 'Cassidy-English'
      );
      const defaultVoice =
        cassidyVoice ||
        voicesForLanguage.find(
          (v: { labels?: { gender?: string } }) => v.labels?.gender === 'Female'
        ) ||
        voicesForLanguage[0];
      onDefinitionChange({
        ...definition,
        language: defaultLanguage,
        ...(defaultVoice && {
          voice: {
            name: defaultVoice.name,
            _id: defaultVoice._id,
            voice_id: defaultVoice.voice_id,
            provider: 'Ultra',
            accent: defaultVoice.labels?.accent,
            gender: defaultVoice.labels?.gender,
          },
        }),
      });
      return;
    }

    // Keep one voice selected: if language is set but voice missing (e.g. voice agent), set default voice for that language
    if (
      agentType === 'voice' &&
      definition.language &&
      !definition?.voice?.name &&
      typeof definition?.voice !== 'string'
    ) {
      const voicesForLanguage = voicesData.filter(
        (v: { language?: string }) => v.language === definition.language
      );
      const defaultVoice =
        voicesForLanguage.find(
          (v: { labels?: { gender?: string } }) => v.labels?.gender === 'Female'
        ) || voicesForLanguage[0];
      if (defaultVoice) {
        onDefinitionChange({
          ...definition,
          voice: {
            name: defaultVoice.name,
            _id: defaultVoice._id,
            voice_id: defaultVoice.voice_id,
            provider: 'Ultra',
            accent: defaultVoice.labels?.accent,
            gender: defaultVoice.labels?.gender,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when voices load or definition missing language/voice
  }, [voicesData, definition?.language, definition?.voice, agentType]);

  const handleInputChange = (field: string, value: string) => {
    const next = { ...definition, [field]: value };
    if (field === 'language') {
      next.voice = undefined;
      if (agentType === 'voice' && voicesData.length > 0) {
        const voicesForLanguage = voicesData.filter(
          (v: { language?: string }) => v.language === value
        );
        const defaultVoice =
          voicesForLanguage.find(
            (v: { labels?: { gender?: string } }) =>
              v.labels?.gender === 'Female'
          ) || voicesForLanguage[0];
        if (defaultVoice) {
          next.voice = {
            name: defaultVoice.name,
            _id: defaultVoice._id,
            voice_id: defaultVoice.voice_id,
            provider: 'Ultra',
            accent: defaultVoice.labels?.accent,
            gender: defaultVoice.labels?.gender,
          };
        }
      }
    }
    onDefinitionChange(next);
  };

  const handleVoiceChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.value;
    const selectedVoice = getAvailableVoices.find(
      (v: { name?: string }) => v.name === name
    );
    if (selectedVoice) {
      onDefinitionChange({
        ...definition,
        voice: {
          name: selectedVoice.name,
          _id: selectedVoice._id,
          voice_id: selectedVoice.voice_id,
          provider: 'Ultra',
          accent: selectedVoice.labels?.accent,
          gender: selectedVoice.labels?.gender,
        },
      });
    }
  };

  const voiceSelectValue =
    typeof definition?.voice === 'object' && definition?.voice?.name
      ? definition.voice.name
      : typeof definition?.voice === 'string'
        ? definition.voice
        : '';

  return (
    <Box
      sx={{ maxWidth: '1400px', width: '100%', margin: '0 auto', py: 3, px: 4 }}
    >
      <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
        {/* Heading */}
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: 'custom.textBlue',
            mb: 1,
          }}
        >
          Define Your Agent
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            color: 'lightGray.text',
            mb: 3,
          }}
        >
          Configure your {agentType === 'voice' ? 'voice' : 'WhatsApp'} agent's
          basic settings
        </Typography>

        {/* Form */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            p: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Agent Name */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Agent Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., Customer Support Agent"
                value={definition?.name || ''}
                onChange={e => handleInputChange('name', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* Personality */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Personality *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Describe your agent's personality and tone (e.g., friendly, professional, helpful)"
                value={definition?.personality || ''}
                onChange={e => handleInputChange('personality', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: 0,
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* Language */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Language *
              </Typography>
              <Select
                fullWidth
                value={definition?.language || ''}
                onChange={e =>
                  handleInputChange('language', e.target.value as string)
                }
                displayEmpty
                disabled={!voicesData.length}
                sx={{
                  height: '40px',
                  borderRadius: '6px',
                  bgcolor: 'secondary.main',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'lightGray.main',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D0D5DD',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select language
                </MenuItem>
                {getUniqueLanguages.map((item: { language: string }) => (
                  <MenuItem key={item.language} value={item.language}>
                    {item.language}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Voice (only for voice agents) */}
            {agentType === 'voice' && (
              <Box>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'custom.textBlue',
                    mb: 0.5,
                  }}
                >
                  Voice *
                </Typography>
                <Select
                  fullWidth
                  value={voiceSelectValue}
                  onChange={handleVoiceChange}
                  displayEmpty
                  disabled={!definition?.language}
                  sx={{
                    height: '40px',
                    borderRadius: '6px',
                    bgcolor: 'secondary.main',
                    fontSize: '14px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'lightGray.main',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select voice
                  </MenuItem>
                  {getAvailableVoices.map(
                    (voice: {
                      name?: string;
                      voice_id?: string;
                      _id?: string;
                      labels?: { gender?: string };
                    }) => (
                      <MenuItem
                        key={voice.voice_id ?? voice._id ?? voice.name}
                        value={voice.name ?? ''}
                      >
                        {voice.name}{' '}
                        {voice.labels?.gender ? `(${voice.labels.gender})` : ''}
                      </MenuItem>
                    )
                  )}
                </Select>
              </Box>
            )}

            {/* Timezone */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Timezone *
              </Typography>
              <Select
                fullWidth
                value={definition?.timezone || ''}
                onChange={e =>
                  handleInputChange('timezone', e.target.value as string)
                }
                displayEmpty
                disabled={timezonesLoading}
                sx={{
                  height: '40px',
                  borderRadius: '6px',
                  bgcolor: 'secondary.main',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'lightGray.main',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D0D5DD',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  {timezonesLoading ? 'Loading...' : 'Select timezone'}
                </MenuItem>
                {timeZones.map((tz: string) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AgentDefinition;
